import { analytics } from "services/analytics";

// PRD-3334 PLG events (tracking sheet "PLG Events"). Sent through Segment, which forwards to PostHog.
// Only PLG customers send these; shared screens check `api.kind === "plg"` first.

export type SignupMethod = "google" | "microsoft" | "email";
/** Backend plgViewCaps: verified accounts see full profiles, everyone else redacted ones. */
export type PlgTier = "blocked" | "full" | "redacted";
export type FilterType = "role" | "tools" | "language";
export type LockedElement = "pdf" | "intro_video";
export type PreferenceTrigger = "first_shortlist" | "second_shortlist";
export type CapType = "session" | "user_daily" | "account_daily";

export interface PlgEventProps {
  signup_started: { method: SignupMethod };
  /** The non-US card ("Edge is available in the US only for now") replaced sign-in; no routing destinations yet. */
  geo_routed: { destination: "blocked"; ip_country?: string };
  signup_blocked: { reason: "personal_email" | "disposable" | "account_exists" | "admin_account"; email_domain?: string };
  account_created: { method?: SignupMethod; email_type: "personal" | "business" };
  /** The company step also sends its business name, phone and size (companyTraits). */
  onboarding_step_completed: { step: string; skipped: boolean; time_on_step_ms: number; company_name?: string; phone?: string; size_band?: string };
  tools_selected: { tools: string[]; count: number; conditional_hit: boolean };
  onboarding_abandoned: { last_step: string };
  talent_network_entered: { entry_point: string };
  filter_applied: { filter_type: FilterType; value: string[]; results_count: number };
  search_returned_empty: { filters_active: FilterType[] };
  profile_viewed: {
    talent_id: string;
    role?: string;
    position_in_list?: number;
    tier: PlgTier;
    session_view_count: number;
    tool_match: boolean;
  };
  standard_tile_clicked: { tile: string };
  standard_tile_feedback: { tile: string; helpful: boolean };
  /** Home's "What happens next?": opens the getting-started tour. */
  what_happens_next_clicked: Record<string, never>;
  /** The walkthrough reached its last step. */
  walkthrough_completed: Record<string, never>;
  /** The walkthrough was closed before its last step (1-based). */
  walkthrough_skipped: { step: number };
  glance_expanded: { talent_id: string };
  shortlist_added: { talent_id: string; shortlist_count_after: number; from: "card" | "profile"; tool_match: boolean };
  shortlist_removed: { talent_id: string; shortlist_count_after: number; time_held_ms?: number };
  preference_answered: { tiles: string[]; trigger: PreferenceTrigger; dismissed: boolean };
  locked_element_clicked: { element: LockedElement; talent_id?: string };
  meeting_panel_opened: { shortlist_count: number; entry_point: string };
  meeting_requested: {
    shortlist_count: number;
    shortlist_ids: string[];
    urgency_request?: string;
    has_voip?: string;
    notes_given: boolean;
    standard_tiles_clicked: string[];
  };
  meeting_panel_abandoned: { shortlist_count: number; last_field_touched: string | null };
  cap_wall_hit: { cap_type: CapType; shortlist_count: number; views_in_window: number };
  error_shown: { error_type: string; screen: string };
}

export type PlgEvent = keyof PlgEventProps;

const METHODS: SignupMethod[] = ["google", "microsoft", "email"];
export const asSignupMethod = (v?: string): SignupMethod | undefined =>
  METHODS.includes(v as SignupMethod) ? (v as SignupMethod) : undefined;

export function trackPlg<E extends PlgEvent>(event: E, props: PlgEventProps[E]): void {
  analytics.track(event, props);
}

/**
 * Portal error codes that stop sign-up. ACCOUNT_BLOCKED is not here: on sign-in it can be any block Edge
 * set (a personal-email sign-up answers BUSINESS_EMAIL_REQUIRED instead).
 */
const BLOCKED_REASONS: Record<string, PlgEventProps["signup_blocked"]["reason"]> = {
  ACCOUNT_EXISTS: "account_exists",
  ADMIN_ACCOUNT_EXISTS: "admin_account",
  DISPOSABLE_EMAIL: "disposable",
  // A new public-mailbox sign-up, blocked right after sign-in (the account exists, waiting for review).
  BUSINESS_EMAIL_REQUIRED: "personal_email",
};

/** signup_blocked for a code that stops sign-up; any other code sends nothing. */
export function trackSignupBlocked(code: string | undefined, emailDomain?: string): void {
  const reason = BLOCKED_REASONS[code || ""];
  if (reason) trackPlg("signup_blocked", { reason, ...(emailDomain ? { email_domain: emailDomain } : {}) });
}
